import { BlueLinkSecondaryUnderlined } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import * as React from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { AddressVerifyModalData } from '@/components/Modals/AddressVerifyModal/types/AddressVerifyModal.types';
import { modalTypes } from '@/enums';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getStoredAddressSuggestion,
    getStoredOriginalAddress,
    getUpdateAccountHasLoaded,
} from '@/redux/modules/account/updateAccount/updateAccount';
import { updateBidderAddress } from '@/redux/modules/account/updateAccount/updateAccount.actions';
import { dismissModal, openAddressModal, openAddressVerifyModal } from '@/redux/modules/modal';
import { getOpenModal } from '@/redux/modules/modal.selectors';
import { BidderAddress } from '@/types/BidderAddresses';
import noop from '@/utils/noop';

import BidderAddressListItem from './BidderAddressListItem';

export type BidderAddressListProps = {
    addresses: BidderAddress[];
    canEdit: boolean;
    selectAddress?: (address: BidderAddress) => void;
    selectedAddress?: BidderAddress | undefined;
    setDefaultOnSelect: boolean;
    showRemoveButton?: boolean;
    showSetDefaultButton?: boolean;
    showTitle?: boolean;
};

const BidderAddressList = ({
    addresses = [],
    canEdit,
    selectAddress = noop,
    selectedAddress,
    setDefaultOnSelect = false,
    showRemoveButton = false,
    showSetDefaultButton,
}: BidderAddressListProps) => {
    const dispatch = useAppDispatch();
    const [selectedAddressId, setSelectedAddressId] = React.useState(() => {
        if (selectedAddress?.addressId) {
            return selectedAddress.addressId;
        }
        if (addresses?.length) {
            const defaultAddress = addresses.find((address) => address.isDefault);
            return defaultAddress?.addressId ?? 0;
        }
        return 0;
    });
    const saveSucceeded = useAppSelector(getUpdateAccountHasLoaded);
    const suggestedAddress = useAppSelector(getStoredAddressSuggestion);

    const handleAddressSelected = React.useCallback(
        (address: BidderAddress) => {
            if (setDefaultOnSelect) {
                dispatch(updateBidderAddress(address.bidderId, { ...address, isDefault: true }, 'defaultUpdate'));
            } else {
                // for address selection outside the profile page
                selectAddress(address);
            }
            setSelectedAddressId(address.addressId);
        },
        [dispatch, selectAddress, setDefaultOnSelect]
    );

    const buildAddressList = React.useCallback(
        (address: BidderAddress) => {
            return (
                <BidderAddressListItem
                    address={address}
                    canEdit={canEdit}
                    key={`address-${address.addressId}`}
                    onSelect={handleAddressSelected}
                    selected={selectedAddressId === address.addressId}
                    selectedId={selectedAddressId || 0}
                    showRemoveButton={showRemoveButton}
                    showSetDefaultButton={showSetDefaultButton}
                />
            );
        },
        [canEdit, handleAddressSelected, selectedAddressId, showRemoveButton, showSetDefaultButton]
    );

    const openAddAddressModal = (id: number) => {
        dispatch(openAddressModal({ addressID: id }));
    };

    const openModal = useAppSelector(getOpenModal);
    const originallyEnteredAddress = useAppSelector(getStoredOriginalAddress);
    const [dismissedModal, setDismissedModal] = useState(false);

    useEffect(() => {
        if (saveSucceeded && openModal === modalTypes.ADDRESS) {
            dispatch(dismissModal());
            setDismissedModal(true);
        }
    }, [dispatch, openModal, saveSucceeded, suggestedAddress]);

    useEffect(() => {
        if (dismissedModal && Boolean(suggestedAddress)) {
            const data: AddressVerifyModalData = {
                suggestedAddress,
                userEnteredAddress: originallyEnteredAddress,
            };
            dispatch(openAddressVerifyModal(data));
        }
    }, [dismissedModal, dispatch, originallyEnteredAddress, suggestedAddress]);

    let sortedAddresses: BidderAddress[] = addresses;
    const defaultAddress = (sortedAddresses || []).find((address) => address.isDefault);

    if (defaultAddress) {
        sortedAddresses = [defaultAddress, ...addresses.filter((address) => !address.isDefault)];
    }

    return (
        <StyledBidderAddressList id="bidder-address-list">
            {Boolean(sortedAddresses.length) && (
                <StyledAddressList>{sortedAddresses.map(buildAddressList)}</StyledAddressList>
            )}
            {Boolean(canEdit) && (
                <StyledAddAddressContainer>
                    <BlueLinkSecondaryUnderlined onClick={() => openAddAddressModal(0)}>
                        <FormattedMessage id="addNewAddress" />
                    </BlueLinkSecondaryUnderlined>
                </StyledAddAddressContainer>
            )}
        </StyledBidderAddressList>
    );
};

export default React.memo<BidderAddressListProps>(BidderAddressList);

const StyledBidderAddressList = styled.div`
    max-width: 600px;
`;

const StyledAddressList = styled.div`
    margin-bottom: 32px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-bottom: 24px;
    }
`;

const StyledAddAddressContainer = styled.div`
    display: flex;
    align-items: center;
    max-width: 600px;
`;
